import React, { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  Container,
  useMediaQuery,
  Box,
  Button,
  TextareaAutosize,
  InputLabel,
  OutlinedInput,
} from "@mui/material";
import "../../styles/inputBox.css";
import { useParams } from "react-router-dom";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ProfileStepDots from "./ProfileStepDots";

interface ProfileThirdProps {
  onBack: () => void;
  onNext: () => void;
  setProfileData: (data: any) => void;
  initialData: any;
  mode: any;
  currentPage?: number;
  onGoToPage?: (page: number) => void;
}

const ProfileThird: React.FC<ProfileThirdProps> = ({
  onBack,
  onNext,
  setProfileData,
  initialData,
  currentPage = 3,
  onGoToPage,
}: ProfileThirdProps) => {
  const { id } = useParams();
  const isMobile = useMediaQuery("(max-width:578px)");
  const [employerValueProposition, setEmployerValueProposition] = useState("");
  const [companyMileStones, setCompanyMilestones] = useState("");
  const [companyCulture, setCompanyCulture] = useState("");
  const [secondaryBenefits, setSecondaryBenefits] = useState("");

  const [errors, setErrors] = useState({
    employerValueProposition: "",
    companyMileStones: "",
    companyCulture: "",
    secondaryBenefits: "",
  });

  const formIsValid = () => {
    const newErrors = {
      employerValueProposition: !employerValueProposition?.trim() ? "Employer Value Proposition is required" : "",
      companyMileStones: !companyMileStones?.trim() ? "Company Milestones is required" : "",
      companyCulture: !companyCulture?.trim() ? "Company Culture is required" : "",
      secondaryBenefits: !secondaryBenefits?.trim() ? "Secondary Benefits is required" : "",
    };
    setErrors(newErrors);
    return Object.values(newErrors).every((error) => error === "");
  };

  useEffect(() => {
    if (initialData) {
      setEmployerValueProposition(initialData?.employerValueProposition)
      setCompanyMilestones(initialData?.companyMileStones)
      setCompanyCulture(initialData?.companyCulture)
      setSecondaryBenefits(initialData?.secondaryBenefits)
    }
  }, [])

  const buildStepData = () => ({
    employerValueProposition,
    companyMileStones,
    companyCulture,
    secondaryBenefits,
  });

  const syncToParent = () => {
    setProfileData({ ...initialData, ...buildStepData() });
  };

  const handleStepNavigate = (page: number) => {
    syncToParent();
    onGoToPage?.(page);
  };

  useEffect(() => {
    if (!initialData || Object.keys(initialData).length === 0) return;

    const nextData = { ...initialData, ...buildStepData() };
    const hasChanges =
      JSON.stringify(nextData) !== JSON.stringify(initialData);

    if (hasChanges) {
      setProfileData(nextData);
    }
  }, [
    employerValueProposition,
    companyMileStones,
    companyCulture,
    secondaryBenefits,
    initialData,
    setProfileData,
  ]);

  const handleBack = (event: any) => {
    event.preventDefault();
    syncToParent();
    onBack();
  };

  const handleSubmit = async (event: any) => {
    event.preventDefault();
    if (!formIsValid()) return;

    syncToParent();
    onNext();
  };

  return (
    <div>
      <Container
        maxWidth="md"
        style={{
          padding: "20px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Grid
          spacing={2}
          bgcolor="#FFFFFF"
          width="784px"
          borderRadius="22px"
          border="1px solid #D6D6D7"
          margin={0}
        >
          <Grid
            item
            height={isMobile ? "auto" : "auto"}
            style={{ padding: 30 }}
          >
            <Grid
              container
              direction="row"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap="10px"
            >
              <Grid item display="flex" alignItems="center" gap="10px">
                <ArrowBackIcon
                  onClick={handleBack}
                  style={{ cursor: "pointer" }}
                />
                <Typography color="#273848" fontWeight={700} fontSize="20.94px">
                  {id ? "Update Profile" : "Create Profile"}
                </Typography>
              </Grid>
              <ProfileStepDots
                currentPage={currentPage}
                onGoToPage={handleStepNavigate}
              />
            </Grid>
            <Grid my={2}></Grid>
            <Grid item>
              <Typography className="input-label-custom">
                Employer Value Proposition
              </Typography>
              <Grid mb={1}></Grid>
              <OutlinedInput
                placeholder="Why do employees want to work at your company?..."
                multiline
                minRows={4}
                maxRows={10}
                className="text-area-size"
                value={employerValueProposition}
                onChange={(e) => {
                  const value = e.target.value;
                  setEmployerValueProposition(value);
                  if (value.trim() !== "") {
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      employerValueProposition: "",
                    }));
                  }
                }}
              />
            </Grid>
            <InputLabel
              style={{
                color: "#f44336",
                paddingTop: "3px",
                fontSize: "12px",
                paddingLeft: "10px",
              }}
            >
              {errors.employerValueProposition}
            </InputLabel>
            <Grid my={2}></Grid>
            <Grid item>
              <Typography className="input-label-custom">
                Company Milestones
              </Typography>
              <Grid mb={1}></Grid>
              <OutlinedInput
                placeholder="What is the company proud of?..."
                multiline
                minRows={4}
                maxRows={10}
                className="text-area-size"
                value={companyMileStones}
                onChange={(e) => {
                  const value = e.target.value;
                  setCompanyMilestones(value);
                  if (value.trim() !== "") {
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      companyMileStones: "",
                    }));
                  }
                }}
              />
            </Grid>
            <InputLabel
              style={{
                color: "#f44336",
                paddingTop: "3px",
                fontSize: "12px",
                paddingLeft: "10px",
              }}
            >
              {errors.companyMileStones}
            </InputLabel>
            <Grid my={2}></Grid>
            <Grid item>
              <Typography className="input-label-custom">
                Company Culture
              </Typography>
              <Grid mb={1}></Grid>
              <OutlinedInput
                placeholder="Company Culture..."
                multiline
                minRows={4}
                maxRows={10}
                className="text-area-size"
                value={companyCulture}
                onChange={(e) => {
                  const value = e.target.value;
                  setCompanyCulture(value);
                  if (value.trim() !== "") {
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      companyCulture: "",
                    }));
                  }
                }}
              />
            </Grid>
            <InputLabel
              style={{
                color: "#f44336",
                paddingTop: "3px",
                fontSize: "12px",
                paddingLeft: "10px",
              }}
            >
              {errors.companyCulture}
            </InputLabel>
            <Grid my={2}></Grid>
            <Grid item>
              <Typography className="input-label-custom">
                Secondary Benefits
              </Typography>
              <Grid mb={1}></Grid>
              <OutlinedInput
                placeholder="Secondary Benefits..."
                multiline
                minRows={4}
                maxRows={10}
                className="text-area-size"
                value={secondaryBenefits}
                onChange={(e) => {
                  const value = e.target.value;
                  setSecondaryBenefits(value);
                  if (value.trim() !== "") {
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      secondaryBenefits: "",
                    }));
                  }
                }}
              />
            </Grid>
            <InputLabel
              style={{
                color: "#f44336",
                paddingTop: "3px",
                fontSize: "12px",
                paddingLeft: "10px",
              }}
            >
              {errors.secondaryBenefits}
            </InputLabel>
            <Grid mb="10px"></Grid>
            <Grid
              container
              direction="row"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Grid xs={2.5}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  style={{
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #000000",
                    color: "#273848",
                    boxShadow: "none",
                    borderRadius: "5px",
                    padding: "17px 17px",
                    lineHeight: "0px",
                    textTransform: "none",
                    letterSpacing: "0px",
                  }}
                  onClick={(event) => handleBack(event)}
                >
                  Back
                </Button>
              </Grid>
              <Grid
                item
                xs={isMobile ? 0 : 3}
                style={{ maxWidth: "fitContent" }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  style={{
                    backgroundColor: "#84E1CC",
                    boxShadow: "none",
                    borderRadius: "5px",
                    padding: "17px 17px",
                    lineHeight: "0px",
                    textTransform: "none",
                    letterSpacing: "0px",
                  }}
                  onClick={(event) => handleSubmit(event)}
                >
                  Continue
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </div>
  );
};

export default ProfileThird;
